// v2 but not in use. the multiple expense entry form is now in NewAddExpenses.jsx instead of this file. This file is kept for reference only, but not used in the app.
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Button, Form, Nav, Tab, Table } from "react-bootstrap";
import swal from "sweetalert";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../assets/global/url";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import { format, parse, addDays } from "date-fns";
import Select from "react-select";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import CustomDatePicker from "../../../components/CustomDatePicker";
import useFormattedNumberInput from "../../../hooks/customHook/Number_Formatter";
import { parseNumber } from "../../../utils/numberFormatter";

// Shared helper - used by every expense entry
const dateToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const year = today.getFullYear();

  return `${year}-${month}-${day}`;
};

// Compares two dates by calendar day only (ignores time-of-day), so a
// plain "YYYY-MM-DD" string and a Date object produced by the picker
// still compare correctly even when their internal times differ.
const toDateOnly = (value) => {
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Custom input for DatePicker to prevent user typing/input.
// Shared across every expense entry, defined once at module scope.
const CustomInput = React.forwardRef(
  ({ value, onClick, generateYears }, ref) => (
    <input
      type="text"
      className="form-control p-3 w-100"
      style={{
        cursor: "pointer",
        caretColor: "transparent",
      }}
      onClick={() => {
        onClick();

        const date = new Date(value).getFullYear();

        generateYears(date); // Reset/Initialize Year List based on selected date
      }}
      value={value}
      ref={ref}
      placeholder="Select Date"
      required
    />
  ),
);

// One full "Add Expenses" form block.
// Each block manages its own local state/hooks (Rules of Hooks safe, since
// each is its own component instance) and exposes its current values to the
// parent through a ref via getPayload().
const ExpenseEntryFields = forwardRef(function ExpenseEntryFields(
  {
    entryKey,
    index,
    canRemove,
    onRemove,
    currency_db,
    expensesType_db,
    userOptions,
    expenseType1Options,
    validated,
    fetchNewCode,
    prefill, // only populated for the first entry (Fixed Asset -> Expense flow)
  },
  ref,
) {
  const hasFixedCurrency = !!(prefill?.currencyId && prefill.currencyId !== "null"); // prettier-ignore
  const hasFixedAmount = !!(prefill?.totalFixedAssetAmount && prefill.totalFixedAssetAmount !== "null"); // prettier-ignore

  const initialAmount = hasFixedAmount ? prefill.totalFixedAssetAmount : "";

  const [transaction_id, setTransaction_id] = useState("");
  const [clientTransactionId, setClientTransactionId] = useState("");
  const [expensesCheque, setExpensesCheque] = useState(""); // para sa yilujia kasi un ang pag babasihan nila
  const [totalAmountDisplay, handleTotalAmountChange] =
    useFormattedNumberInput(initialAmount);
  const [integer = "", decimal = ""] = totalAmountDisplay.split(".");

  const [desc, setDesc] = useState("");
  const [expenses_date, setExpenses_date] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [foreign, setForeign] = useState("Local");
  const [expensesSubType_id, setExpensesSubType_id] = useState();
  const [expensesType_name, setExpensesType_name] = useState("");
  const [selected_currency_id, setSelected_currency_id] = useState(
    hasFixedCurrency
      ? prefill.currencyId
      : "11111111-1111-1111-1111-111111111111",
  );
  const [currencyRate, setCurrencyRate] = useState(1);
  const [accountable, setAccountable] = useState(null);
  const [isExternalAccountable, setIsExternalAccountable] = useState(false);
  const [externalAccountableText, setExternalAccountableText] = useState("");

  // Get a fresh transaction code for this entry + default the date
  useEffect(() => {
    let isActive = true;

    fetchNewCode().then((code) => {
      if (!isActive) return;
      setTransaction_id(code);
      setClientTransactionId(code);
    });

    setExpenses_date(dateToday());

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Due Date always tracks 15 days ahead of Expenses Date
  useEffect(() => {
    if (!expenses_date) return;
    setDueDate(addDays(new Date(expenses_date), 15));
  }, [expenses_date]);

  // Fill in the currency rate once currencies load, for fixed-asset prefilled entries
  useEffect(() => {
    if (hasFixedCurrency && currency_db.length > 0) {
      const curr = currency_db.find(
        (data) => String(data.id) === String(prefill.currencyId),
      );
      if (curr) setCurrencyRate(curr.currency_rate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency_db]);

  const handleExpensesChange = (selectedOption) => {
    const selectedId = selectedOption?.value;
    setExpensesSubType_id(selectedOption);

    const selected = expensesType_db.find(
      (data) => data.id === String(selectedId, 10),
    );
    setExpensesType_name(selected ? selected.sub_type : "");
  };

  const handleChangeCurrency = (value) => {
    const curr = currency_db.find((data) => String(data.id) === String(value));
    setSelected_currency_id(curr.id);
    setCurrencyRate(curr.currency_rate);
  };

  const handleAmountBlur = (e) => {
    // Strip existing commas to prevent NaN errors when parsing (e.g., "1,500.5")
    const rawValue = e.target.value.replace(/,/g, "");

    if (rawValue && !isNaN(rawValue)) {
      // Force exactly two decimal places
      const formattedValue = parseFloat(rawValue).toFixed(2);

      // Pass the formatted value back to the hook using a synthetic event
      handleTotalAmountChange({ target: { value: formattedValue } });
    }
  };
  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const formatTotalAmount = totalAmountDisplay.replace(/,/g, "");

      return {
        totalAmount:
          parseFloat(formatTotalAmount) || (hasFixedAmount ? prefill.totalFixedAssetAmount : ""), // prettier-ignore
        desc,
        expenses_date,
        dueDate,
        expensesSubType_id: expensesSubType_id?.value,
        transaction_id,
        clientTransactionId,
        foreign,
        selected_currency_id: hasFixedCurrency
          ? prefill.currencyId
          : selected_currency_id,
        productName: null,
        unitPrice: null,
        assetQuantity: null,
        accountable: !isExternalAccountable
          ? (accountable?.value ?? null)
          : null,
        currencyRate: currencyRate === "" ? null : currencyRate,
        fixedAssetIds: prefill?.fixedAssetIds || [],
        expensesCheque, // para sa yilujia kasi un ang pag babasihan nila
        isExternalAccountable,
        externalAccountableText: isExternalAccountable
          ? externalAccountableText
          : null,
      };
    },
  }));

  const currencySelectValue = hasFixedCurrency
    ? prefill.currencyId
    : selected_currency_id;
  const currencyLocked = hasFixedCurrency && hasFixedAmount;

  return (
    <div
      className="mb-4 pb-3"
      style={canRemove ? { borderBottom: "3px dashed #343a40" } : undefined}
    >
      {canRemove && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-bold text-muted">Expense #{index + 1}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={onRemove}
          >
            <i className="fa-solid fa-trash me-1"></i>
            Remove
          </button>
        </div>
      )}

      <div className="col-sm">
        <Form.Group className="mb-3" controlId={`payWith-${entryKey}`}>
          <Form.Label className="fw-bold">Foreign Type :</Form.Label>
          <div className="d-flex">
            <Form.Check
              type="radio"
              name={`foreignType-${entryKey}`}
              id={`Local-${entryKey}`}
              label="Local"
              value="Local"
              checked={foreign === "Local"}
              onClick={() => setForeign("Local")}
              className="me-3"
            />
            <Form.Check
              type="radio"
              name={`foreignType-${entryKey}`}
              id={`Overseas-${entryKey}`}
              label="Overseas"
              value="Overseas"
              checked={foreign === "Overseas"}
              onClick={() => setForeign("Overseas")}
              className="me-3"
            />
          </div>
        </Form.Group>
      </div>

      <div className="container-fluid mt-2 p-0">
        <div className="row mx-auto">
          <div className="col-sm">
            <span>Transaction ID :</span> <span className="text-danger">*</span>
            <div className="input-group mb-2">
              <input
                type="text"
                required
                value={clientTransactionId}
                onChange={(e) => setClientTransactionId(e.target.value)}
                className="form-control p-3"
                id={`client-transaction-id-${entryKey}`}
              />
            </div>
          </div>

          <div className="col-sm">
            <div className="d-flex align-items-center mb-2">
              <span className="me-3 ">ACCOUNTABLE</span>

              <div className="form-check mb-0 text-muted badge ">
                <label
                  className="form-check-label ms-1 "
                  htmlFor={`externalAccountable-${entryKey}`}
                >
                  External Accountable Person
                </label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`externalAccountable-${entryKey}`}
                  checked={isExternalAccountable}
                  onChange={(e) => {
                    setIsExternalAccountable(e.target.checked);
                  }}
                />
              </div>
            </div>
            {isExternalAccountable ? (
              <input
                type="text"
                required
                value={externalAccountableText}
                onChange={(e) => setExternalAccountableText(e.target.value)}
                className="form-control p-3"
              />
            ) : (
              <Select
                options={userOptions}
                value={accountable}
                onChange={(selected) => setAccountable(selected)}
                placeholder="Select Accountable Person"
                styles={selectCustomStyles(
                  accountable ?? "optional",
                  validated,
                  "0.55rem",
                )}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </div>
        </div>

        <div className="row mx-auto">
          <div className="col-sm mb-2">
            <span>
              Expenses Type 1 <span className="text-danger">*</span>
            </span>
            <Select
              options={expenseType1Options}
              value={expensesSubType_id}
              onChange={handleExpensesChange}
              placeholder={`Select Expense Type 1`}
              styles={selectCustomStyles(
                expensesSubType_id,
                validated,
                "0.55rem",
              )}
              required
            />
          </div>

          <div className="col-sm mb-2">
            <span>
              Expenses Type 2 <span className="text-danger">*</span>
            </span>
            <input
              type="text"
              required
              readOnly
              value={expensesType_name}
              className="form-control p-3"
              id={`expenses-type-2-${entryKey}`}
            />
          </div>
        </div>

        <div className="row mx-auto mt-3">
          <div className="col-sm">
            <label htmlFor={`expenses_date-${entryKey}`}>
              Expenses Date <span className="text-danger">*</span>
            </label>
            <CustomDatePicker
              label={"Expenses Date"}
              selected={expenses_date ? new Date(expenses_date) : ""}
              handleDateChange={(date) => setExpenses_date(date)}
              setter={setExpenses_date}
              CustomInput={CustomInput}
              isRequired={true}
              validated={validated}
              iconTopOffset={"1.3rem"}
              maxDate={dueDate ? new Date(dueDate) : undefined}
            />
          </div>
          <div className="col-sm mb-2">
            <label htmlFor={`due_date-${entryKey}`}>
              Due Date<span className="text-danger">*</span>
            </label>
            <CustomDatePicker
              label={"Due Date"}
              selected={dueDate ? new Date(dueDate) : ""}
              handleDateChange={(date) => setDueDate(date)}
              setter={setDueDate}
              CustomInput={CustomInput}
              isRequired={true}
              validated={validated}
              iconTopOffset={"1.3rem"}
              minDate={expenses_date ? new Date(expenses_date) : undefined}
            />
          </div>
        </div>

        <div className="row mx-auto mt-3">
          <div className="col-sm">
            <div className="col-sm mb-2">
              <span>
                Amount <span className="text-danger">*</span>
                {selected_currency_id &&
                selected_currency_id !==
                  "11111111-1111-1111-1111-111111111111" ? (
                  <span className="text-muted mb-1">
                    {" "}
                    Current Rate: {currencyRate}
                  </span>
                ) : null}
              </span>
              <div className="input-group z-0 mb-2">
                <div className="input-group-prepend">
                  <div className="input-group-text h-100">
                    <select
                      required
                      onChange={(e) => handleChangeCurrency(e.target.value)}
                      className="form-select"
                      value={currencySelectValue}
                      disabled={currencyLocked}
                    >
                      <option value="" disabled>
                        Select Currency
                      </option>
                      {currency_db.map((data) => (
                        <option key={data.id} value={data.id}>
                          {`${data.currency_name}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  type="text"
                  value={totalAmountDisplay}
                  required
                  onChange={handleTotalAmountChange}
                  onBlur={handleAmountBlur} // <-- Add this line
                  className="form-control p-3 z-0"
                  id={`amount-${entryKey}`}
                  readOnly={currencyLocked}
                  maxLength={decimal ? 20 : 19}
                />
              </div>
            </div>
          </div>
          <div className="col-sm ">
            <span>Cheque Number :</span>{" "}
            <div className="input-group mb-2">
              <input
                type="text"
                value={expensesCheque}
                onChange={(e) => setExpensesCheque(e.target.value)}
                className="form-control p-3"
                id={`cheque-number-${entryKey}`}
              />
            </div>
          </div>
        </div>

        <div className="row mx-auto mt-3">
          <div className="col-sm mb-2">
            <label htmlFor={`description-${entryKey}`}>Description</label>
            <Form.Control
              id={`remarks-${entryKey}`}
              as="textarea"
              rows={3}
              style={{
                fontSize: "16px",
                height: "200px",
                maxHeight: "200px",
                resize: "none",
                overflowY: "auto",
              }}
              placeholder="Enter Description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const NewAddExpenses = () => {
  const { currencyId, totalFixedAssetAmount } = useParams();
  const { state } = useLocation();
  const ids = state?.ids || [];
  const userLoggedID = useDecodeToken();

  const navigate = useNavigate();

  const [validated, setValidated] = useState(false);
  const [expensesType_db, setExpensesType_db] = useState([]);
  const [currency_db, setCurrency_db] = useState([]);
  const [userList, setUserList] = useState([]);

  // Keys for each rendered "Add Expenses" block. Starts with a single entry.
  const [entryKeys, setEntryKeys] = useState([0]);
  const nextKeyRef = useRef(1);
  const entryRefs = useRef({});

  // Pagination for expense entries - same pattern as the Other Fee rows
  // in create_invoice.jsx, but capped at 2 per page instead of 5.
  const EXPENSE_ENTRY_PAGE_SIZE = 2;
  const [entryPage, setEntryPage] = useState(0);

  const fetchNewCode = useCallback(async () => {
    try {
      const res = await axios.get(BASE_URL + "/expenses/getCode");
      return res.data;
    } catch (err) {
      console.log(err);
      return "";
    }
  }, []);

  const fetchUsers = async () => {
    await axios
      .get(BASE_URL + "/masterList/fetchAllUsers")
      .then((response) => {
        setUserList(response.data);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };

  const fetchExpenses = async () => {
    await axios
      .get(BASE_URL + "/expenses2/fetchTable")
      .then((response) => {
        setExpensesType_db(response.data);
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  };

  const fetchCurrency = async () => {
    await axios
      .get(BASE_URL + "/currency/fetchCurrency")
      .then((response) => {
        setCurrency_db(response.data);
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  };

  useEffect(() => {
    fetchExpenses();
    fetchCurrency();
    fetchUsers();
  }, []);

  // User options for ACCOUNTABLE dropdown
  const userOptions = userList.map((user) => ({
    value: user.id,
    label: `${user.fname} ${user.mname ? user.mname + " " : ""}${user.lname}`,
  }));

  // Expense Type 1 Options for dropdown select
  const expenseType1Options = expensesType_db.map((item) => ({
    value: item.id,
    label: `${item.expenses_one.expenses_type_one} - ${item.sub_type}`,
  }));

  const handleAddEntry = () => {
    const newKey = nextKeyRef.current++;
    const updatedKeys = [...entryKeys, newKey];
    setEntryKeys(updatedKeys);
    // Jump to the page that contains the newly added entry, same as addNewFee()
    setEntryPage(
      Math.floor((updatedKeys.length - 1) / EXPENSE_ENTRY_PAGE_SIZE),
    );
  };

  const handleRemoveEntry = (key) => {
    const updatedKeys = entryKeys.filter((k) => k !== key);
    setEntryKeys(updatedKeys);
    delete entryRefs.current[key];

    // Step back a page if the current page no longer has any entries, same as removeFee()
    const newTotalPages = Math.ceil(
      updatedKeys.length / EXPENSE_ENTRY_PAGE_SIZE,
    );
    if (entryPage >= newTotalPages && entryPage > 0) {
      setEntryPage((p) => p - 1);
    }
  };

  const add = async (e) => {
    // Save button
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();

      // A required field on a page that isn't showing right now is still
      // "invalid" as far as the browser is concerned, it's just invisible.
      // Jump to whichever page has the first empty required field so the
      // red outline is actually on screen instead of failing silently.
      const firstInvalid = form.querySelector(":invalid");
      const invalidEntry = firstInvalid?.closest("[data-entry-index]");
      if (invalidEntry) {
        const invalidIndex = parseInt(invalidEntry.dataset.entryIndex, 10);
        setEntryPage(Math.floor(invalidIndex / EXPENSE_ENTRY_PAGE_SIZE));
      }

      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      setValidated(true);
      return;
    }

    // Expenses Date must not be later than Due Date, checked per entry.
    // Mirrors the required-field check above: jump to whichever page
    // holds the offending entry so the error is visible.
    for (let index = 0; index < entryKeys.length; index++) {
      const payload = entryRefs.current[entryKeys[index]]?.getPayload();
      if (!payload) continue;

      const { expenses_date, dueDate } = payload;
      if (
        expenses_date &&
        dueDate &&
        toDateOnly(expenses_date) > toDateOnly(dueDate)
      ) {
        setEntryPage(Math.floor(index / EXPENSE_ENTRY_PAGE_SIZE));
        swal({
          icon: "error",
          title: "Invalid Date",
          text: `Expense #${index + 1}: Expenses Date cannot be later than Due Date.`,
        });
        setValidated(true);
        return;
      }
    }

    const isMultiple = entryKeys.length > 1;

    swal({
      title: `Create ${isMultiple ? "these" : "this"} new expense${
        isMultiple ? "s" : ""
      }?`,
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      try {
        for (const key of entryKeys) {
          const payload = entryRefs.current[key]?.getPayload();
          if (!payload) continue;

          const res = await axios.post(`${BASE_URL}/expenses/createExpenses`, {
            ...payload,
            userLoggedID,
          });

          if (res.status === 201) {
            swal({
              title: "Opppss!",
              text: "Cutoff already posted for this date",
              icon: "warning",
              buttons: false,
              timer: 2000,
              dangerMode: true,
            });
            return;
          } else if (res.status !== 200) {
            swal({
              title: "Something Went Wrong",
              text: "Please contact your support immediately",
              icon: "error",
              buttons: false,
              timer: 2000,
              dangerMode: true,
            });
            return;
          }
        }

        swal({
          title: "Success",
          text: `Expense${isMultiple ? "s" : ""} created successfully`,
          icon: "success",
          buttons: false,
          timer: 2000,
          dangerMode: true,
        }).then(() => {
          navigate("/accounting/expenses");
        });
      } catch (err) {
        if (err.response && err.response.status === 409) {
          swal({
            icon: "error",
            title: "Transaction ID Conflict",
            text: "The transaction ID you entered already exists in the system. Please use a unique identifier.",
          });
          return;
        }

        swal({
          title: "Something Went Wrong",
          text: "Please contact your support immediately",
          icon: "error",
          buttons: false,
          timer: 2000,
          dangerMode: true,
        });
      }
    });

    setValidated(true);
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link to="/accounting/expenses" className="text-dark me-2">
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            ADD EXPENSES
          </span>
        </div>
      </div>

      <Form noValidate validated={validated} onSubmit={add}>
        {(() => {
          const start = entryPage * EXPENSE_ENTRY_PAGE_SIZE;
          const totalPages = Math.ceil(
            entryKeys.length / EXPENSE_ENTRY_PAGE_SIZE,
          );

          return (
            <>
              {entryKeys.map((key, index) => {
                // Entries off the current page stay mounted (so their data
                // isn't lost) but are visually hidden instead of unmounted.
                const isVisible =
                  index >= start && index < start + EXPENSE_ENTRY_PAGE_SIZE;

                return (
                  <div
                    key={key}
                    data-entry-index={index}
                    style={isVisible ? undefined : { display: "none" }}
                  >
                    <ExpenseEntryFields
                      entryKey={key}
                      index={index}
                      canRemove={entryKeys.length > 1}
                      onRemove={() => handleRemoveEntry(key)}
                      currency_db={currency_db}
                      expensesType_db={expensesType_db}
                      userOptions={userOptions}
                      expenseType1Options={expenseType1Options}
                      validated={validated}
                      fetchNewCode={fetchNewCode}
                      prefill={
                        index === 0
                          ? {
                              currencyId,
                              totalFixedAssetAmount,
                              fixedAssetIds: ids,
                            }
                          : null
                      }
                      ref={(el) => {
                        if (el) entryRefs.current[key] = el;
                      }}
                    />
                  </div>
                );
              })}

              {totalPages > 1 && (
                <div className="w-100 d-flex justify-content-between align-items-center px-2 pb-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={entryPage === 0}
                    onClick={() => setEntryPage((p) => p - 1)}
                  >
                    ‹ Prev
                  </button>
                  <small className="text-muted">
                    {entryPage + 1} / {totalPages}
                  </small>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={entryPage >= totalPages - 1}
                    onClick={() => setEntryPage((p) => p + 1)}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          );
        })()}

        <div className="row mb-4">
          <div className="col-sm-12">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddEntry}
            >
              <i className="fa-solid fa-plus me-2"></i>
              Add Expenses
            </button>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-sm"></div>
          <div className="col-sm"></div>
          <div className="col-sm d-flex flex-row align-items-end mb-2 w-100">
            <button
              className="btn btn-secondary w-100 me-3"
              type="button"
              onClick={() => navigate("/accounting/expenses")}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary w-100">
              Save
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default NewAddExpenses;
